/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len -- verbatim prototype strings and golden rows exceed the line limit */
// Exam-level cross-position timing consistency — the CLIENT-side owner of the
// prototype's `_exam_timing_consistency`. Derived clinical results are
// computed client-side from the raw guide payloads the server stores verbatim,
// so the client computes the exam consensus from the bundle's guide payloads
// and the Python twin is not ported server-side.
//
// FAITHFUL PORT (2026-08-10, retiring the earlier stand-in reconstruction):
// pcg_viewer_server.py (Medaica_PCG_Engineering_Handoff_v0.8.1-dev.18)
//   _preview_timing_available   :301-311  → recordingTimingReliability()
//   _recording_preview_evidence :323-345  → (timing/heartRateBpm fields only)
//   _exam_timing_consistency    :363-448  → buildExamTimingConsistency()
// Handoff v0.8.3 (:363-499) added the reviewer-facing `proposal` block
// (largest consistent cluster, never auto-applied); ported below and pinned
// by regenerated goldens.
// Pinned against goldens generated by the GENUINE Python function over the
// guide fixture corpus (__fixtures__/exam-timing-consistency-goldens.json).
//
// Algorithm (verbatim from the source):
// - Reliability gate per recording: gate != reject, finite metrics.hr_bpm,
//   n_s1 >= 8, orientation score >= 0.25. BPM input is the PIPELINE's
//   metrics.hr_bpm rounded to 1dp (not the client-derived canonical HR).
// - Harmonic-fundamental search: for each reliable BPM as candidate, map
//   every reliable BPM directly (error <= 0.12) or as a half-count
//   (bpm/2, error <= 0.12 → doubled). A candidate stands only with >= 3
//   mapped positions AND at least one doubled; best by (count, -dispersion).
// - Consensus = median of harmonic-normalized rates; per-recording bands:
//   consistent (< 5 bpm and < 8%), review (>= 5 bpm or >= 8%),
//   material_review (> 10 bpm or > 15%, or raw-ratio half/double band
//   [0.45,0.55]∪[1.8,2.2]); doubled ids become auto_corrected with
//   correctedBpm = the exam median. Exam-level roll-up per :425-430.
// - < 2 reliable positions → {status:"insufficient", medianBpm:null,
//   recordings:[]} (the callers' no-cue path; no `proposal` key upstream).
// - Proposal: every reliable BPM anchors a cluster of reliable BPMs within
//   5 bpm or 8% of it; clusters of >= 2 rank by (count desc, dispersion asc).
//   A unique winner → {available:true, proposedExamBpm: round(center) …};
//   a tie between distinct clusters → no proposal, which becomes the
//   `no_reliable_proposal` fallback when the exam status is review /
//   material_review and stays `null` otherwise.
//
// Output = the Python shape plus two client conveniences: `schema` and
// `reliableCount`. Only RELIABLE recordings appear in `recordings` (exactly
// as upstream — which also means unreliable recordings can never show the
// cross-position cue; the stand-in's "no_timing" rows are gone).

/**
 * Python-compatible round() at `digits` decimals: correctly rounded from
 * the binary double, half-EVEN on exact ties. Neither naive value*10^d
 * (multiplies in rounding error) nor bare toFixed (half-up on ties) matches
 * CPython; the golden corpus exercises both failure modes (23.15 → 23.1
 * needs correct rounding, exact-dyadic 56.25 → 56.2 needs half-even). Ties
 * are detected from a 20-digit decimal expansion — a double can only BE an
 * exact tie when its fraction is dyadic, which terminates well within that.
 * digits === 0 reproduces Python's integer round() (97.5 → 98, 70.5 → 70).
 */
const pythonRound = (value, digits) => {
  if (!Number.isFinite(value)) return value
  const negative = value < 0
  const abs = Math.abs(value)
  const expansion = abs.toFixed(20)
  const dot = expansion.indexOf(".")
  const kept = expansion.slice(0, dot + 1 + digits)
  const tail = expansion.slice(dot + 1 + digits)
  const step = Math.pow(10, -digits)
  let result = Number(kept)
  // Last kept digit (skipping the trailing "." when digits === 0 → integer round()).
  const lastKeptDigit = Number(kept.replace(/\.$/, "").slice(-1))
  const roundUp = /^50*$/.test(tail)
    ? lastKeptDigit % 2 === 1 // exact tie → up only from odd
    : Number(tail[0]) >= 5
  if (roundUp) result = Number((result + step).toFixed(digits))
  return negative ? -result : result
}

/** Source :365-368 — true median (averages the middle pair on even counts). */
const median = (values) => {
  const ordered = [...values].sort((a, b) => a - b)
  const middle = Math.floor(ordered.length / 2)
  return ordered.length % 2 ? ordered[middle] : (ordered[middle - 1] + ordered[middle]) / 2
}

/**
 * _preview_timing_available (:301-311) over a stored guide payload
 * (:323-328 field extraction). Returns the reliable 1dp BPM, or null.
 */
export function reliableTimingBpm(payload) {
  const metrics = (payload && payload.metrics) || {}
  const gateStatus = String(((payload && payload.cardiacPlausibilityGate) || {}).status || "").toLowerCase()
  const orientation = Number((payload && payload.score) || 0)
  const heartRate = metrics.hr_bpm
  const nS1 = Math.trunc(Number(metrics.n_s1 || 0))
  const timing =
    gateStatus !== "reject" &&
    typeof heartRate === "number" &&
    Number.isFinite(heartRate) &&
    nS1 >= 8 &&
    orientation >= 0.25
  return timing ? pythonRound(heartRate, 1) : null
}

/**
 * Faithful _exam_timing_consistency (:363-448). `recordings`:
 * [{ recordingId, label, payload }] with raw guide payloads in exam capture
 * order (the source iterated the preview dict in that order).
 */
export function buildExamTimingConsistency(recordings) {
  const reliable = []
  const labels = {}
  for (const recording of recordings || []) {
    const recId = String(recording.recordingId || "")
    labels[recId] = String(recording.label || recording.recordingId || "Recording")
    let bpm = null
    try {
      bpm = reliableTimingBpm(recording.payload)
    } catch (err) {
      bpm = null
    }
    if (bpm !== null) {
      reliable.push([recId, bpm])
    }
  }
  if (reliable.length < 2) {
    return { schema: SCHEMA, status: "insufficient", medianBpm: null, reliableCount: reliable.length, recordings: [] }
  }

  // Harmonic-fundamental search (:384-401)
  const harmonicCandidates = []
  for (const [, candidate] of reliable) {
    const mapped = []
    const candidateDoubled = new Set()
    for (const [recId, bpm] of reliable) {
      const directError = Math.abs(bpm - candidate) / candidate
      const halfError = Math.abs(bpm / 2 - candidate) / candidate
      if (directError <= 0.12) {
        mapped.push(bpm)
      } else if (halfError <= 0.12) {
        mapped.push(bpm / 2)
        candidateDoubled.add(recId)
      }
    }
    if (mapped.length >= 3 && candidateDoubled.size > 0) {
      const fundamental = median(mapped)
      const dispersion = mapped.reduce((sum, value) => sum + Math.abs(value - fundamental) / fundamental, 0)
      harmonicCandidates.push({
        count: mapped.length,
        negDispersion: -dispersion,
        fundamental,
        doubled: candidateDoubled,
      })
    }
  }
  let doubledIds = new Set()
  let harmonicFundamental = null
  if (harmonicCandidates.length > 0) {
    const best = harmonicCandidates.reduce((a, b) =>
      b.count > a.count || (b.count === a.count && b.negDispersion > a.negDispersion) ? b : a
    )
    harmonicFundamental = best.fundamental
    doubledIds = best.doubled
  }

  // Consensus + per-recording classification (:408-442)
  const comparisonRates = new Map(reliable.map(([recId, bpm]) => [recId, doubledIds.has(recId) ? bpm / 2 : bpm]))
  const medianBpm = median([...comparisonRates.values()])
  const results = []
  let overall = "consistent"
  for (const [recId, bpm] of reliable) {
    const comparisonBpm = comparisonRates.get(recId)
    const difference = Math.abs(comparisonBpm - medianBpm)
    const differencePct = medianBpm ? (difference / medianBpm) * 100 : 0
    const ratio = medianBpm ? bpm / medianBpm : 1
    const halfDouble = (ratio >= 0.45 && ratio <= 0.55) || (ratio >= 1.8 && ratio <= 2.2)
    const material = halfDouble || difference > 10.0 || differencePct > 15.0
    const review = material || difference >= 5.0 || differencePct >= 8.0
    const autoCorrected = doubledIds.has(recId)
    const status = autoCorrected ? "auto_corrected" : material ? "material_review" : review ? "review" : "consistent"
    if (status === "material_review") {
      overall = "material_review"
    } else if (status === "auto_corrected" && overall !== "material_review" && overall !== "review") {
      overall = "auto_corrected"
    } else if (status === "review" && overall === "consistent") {
      overall = "review"
    }
    results.push({
      recordingId: recId,
      label: labels[recId] !== undefined ? labels[recId] : recId,
      bpm: pythonRound(bpm, 1),
      comparisonBpm: pythonRound(comparisonBpm, 1),
      differenceBpm: pythonRound(difference, 1),
      differencePct: pythonRound(differencePct, 1),
      halfDoublePattern: halfDouble,
      autoCorrected,
      correctedBpm: autoCorrected ? pythonRound(medianBpm, 1) : null,
      status,
    })
  }
  // Reviewer-facing proposal (handoff v0.8.3, :443-492). A proposal is
  // deliberately more conservative than the discrepancy flag: find the
  // largest internally consistent cluster, but do not silently apply it. Two
  // agreeing recordings may support a suggestion; three or more are described
  // as stronger support. Ties remain unresolved.
  const clusterCandidates = []
  for (const [, anchorBpm] of reliable) {
    const cluster = reliable.filter(
      ([, bpm]) => Math.abs(bpm - anchorBpm) <= 5.0 || Math.abs(bpm - anchorBpm) / Math.max(anchorBpm, 1.0) <= 0.08
    )
    if (cluster.length < 2) continue
    const center = median(cluster.map(([, bpm]) => bpm))
    const dispersion = cluster.reduce((sum, [, bpm]) => sum + Math.abs(bpm - center) / Math.max(center, 1.0), 0)
    clusterCandidates.push({
      count: cluster.length,
      negDispersion: -dispersion,
      center,
      ids: cluster.map(([recId]) => recId).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)),
    })
  }
  let proposal = null
  if (clusterCandidates.length) {
    // Python `sorted(..., reverse=True)` over (count, -dispersion, ...) tuples:
    // the winner has the largest count, then the smallest dispersion. Ties are
    // judged on exactly those two keys; a tie between DIFFERENT clusters
    // (distinct member sets) leaves the proposal unresolved.
    const best = clusterCandidates.reduce((a, b) =>
      b.count > a.count || (b.count === a.count && b.negDispersion > a.negDispersion) ? b : a
    )
    const tiedIds = new Set(
      clusterCandidates
        .filter((item) => item.count === best.count && item.negDispersion === best.negDispersion)
        .map((item) => item.ids.join("\u0000"))
    )
    if (tiedIds.size === 1) {
      const supportIds = new Set(best.ids)
      const excludedIds = reliable.map(([recId]) => recId).filter((recId) => !supportIds.has(recId))
      const supportCount = supportIds.size
      // Python round() to an integer: half-even.
      const proposedBpm = pythonRound(best.center, 0)
      proposal = {
        available: true,
        type: "reliable_position_consensus",
        proposedExamBpm: proposedBpm,
        supportCount,
        supportLevel: supportCount >= 3 ? "supported" : "two_recording_consensus",
        supportRecordingIds: [...supportIds].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)),
        excludedRecordingIds: excludedIds,
        rationale:
          `${supportCount} reliable recordings agree near ${proposedBpm} bpm; ` +
          `${excludedIds.length} recording${excludedIds.length !== 1 ? "s" : ""} differ materially.`,
        rule: "largest cluster within 5 bpm or 8%; suggestion requires at least two recordings and is never applied automatically",
      }
    }
  }
  if (proposal === null && (overall === "review" || overall === "material_review")) {
    proposal = {
      available: false,
      type: "no_reliable_proposal",
      rationale: "The reliable recordings do not form one uniquely supported timing cluster.",
      rule: "leave unresolved unless the reviewing clinician can resolve the discrepancy from the waveform and audio",
    }
  }
  return {
    schema: SCHEMA,
    status: overall,
    medianBpm: pythonRound(medianBpm, 1),
    harmonicFundamentalBpm: harmonicFundamental ? pythonRound(harmonicFundamental, 1) : null,
    reliableCount: reliable.length,
    recordings: results,
    proposal,
  }
}

const SCHEMA = "medaica.pcg.exam_timing_consistency.v1-client"
